/**
 * 满额换购、满额赠送计算规则
 */
import { getDishesMarketPrice, getDishesCount } from '../../helper/dish-helper.js';
import { MarkUpActivityDishType, RuleSubjectId, PolicySubjectId, MarkUpPolicyDetail, PurchaseMethod } from '../const/change-purchase.js';
import { getPurchaseAndBenefitShared } from '../../helper/order-helper.js';

/**
 * 整合满额赠送, 满额加价, 满量赠送, 满量加价
 *
 */
export const getPurchaseMethod = (ruleSubjectId, policySubjectId) => {
  switch (ruleSubjectId) {
    case RuleSubjectId.FullSales: {
      // 满额
      if (policySubjectId === PolicySubjectId.SubjectAdd) {
        // 满额加价
        return { code: PurchaseMethod.FullSalesAdd, text: '满额加价' };
      } else if (policySubjectId === PolicySubjectId.SubjectSend) {
        // 满额赠送
        return { code: PurchaseMethod.FullSalesSend, text: '满额赠送' };
      }
      break;
    }
    case RuleSubjectId.FullVolume: {
      if (policySubjectId === PolicySubjectId.SubjectAdd) {
        // 满量加价
        return { code: PurchaseMethod.FullVolumeAdd, text: '满量加价' };
      } else if (policySubjectId === PolicySubjectId.SubjectSend) {
        // 满量赠送
        return { code: PurchaseMethod.FullVolumeSend, text: '满量赠送' };
      }
      break;
    }
    default:
      break;
  }
};

/**
 * purchaseCalculateDetail 加价购具体的计算规则
 * @param {优惠方案的优惠规则} benefitRule
 * @param {参与该优惠方案的菜品} calcDishes
 */
export const purchaseCalculateDetail = (benefitRule, calcDishes) => {
  if (!benefitRule || !calcDishes.length) {
    return false;
  }

  const { logicValue, ruleSubjectId } = benefitRule.calculatRules;

  // 满额, 不区分是赠送还是换购
  if (ruleSubjectId === RuleSubjectId.FullSales) {
    const purchaseWidthDishAmount = getDishesMarketPrice(calcDishes);

    if (purchaseWidthDishAmount >= logicValue) {
      return true;
    }
  }

  // 满量, 不区分是赠送还是换购
  if (ruleSubjectId === RuleSubjectId.FullVolume) {
    const purchaseWidthDishCount = getDishesCount(calcDishes);

    if (purchaseWidthDishCount >= logicValue) {
      return true;
    }
  }

  return false;
};

/**
 * 每个活动的计算校验规则，只针对单菜品
 * 切换菜品优惠的时候，就要校验一次每个活动的规则
 * 页面展示：校验通过，展示【去换购】，校验不通过，则不展示【去换购】
 */
export const purchaseCalculatorValidate = (serviceProps, orderedDishesProps) => {
  const { markUpBenefitProps } = serviceProps;

  if (!markUpBenefitProps) {
    return [];
  }

  const { dishes } = orderedDishesProps;
  const { dishesRules, benefitRules } = markUpBenefitProps;
  const newDishesRules = [];

  // 计算校验规则，第一步，先把菜品信息组合起来
  (benefitRules || []).forEach((benefitRule, index) => {
    const { dishIds } = benefitRule; // 该活动下的所有菜品ID
    const purchaseBenefitId = benefitRule.purchaseBenefitId; // 优惠活动ID
    // 参与计算的菜品
    const calcDishes = [];

    (dishIds || []).forEach(dishId => {
      const dish = dishes.filter(dish_ => dish_.brandDishId === dishId)[0];

      const currentBenefit = dishesRules[dishId].find(dishRule => {
        if (dishRule.benefit.id === purchaseBenefitId && dishRule.checked) {
          return dishRule;
        }
      });

      // 找到了多个菜品 对应 一个活动的关系
      if (dish && !!currentBenefit) {
        calcDishes.push(dish);
      }
    });

    newDishesRules[index] = { ...benefitRule, showPurchaseBtn: purchaseCalculateDetail(benefitRule, calcDishes) };
  });

  return newDishesRules;
};

/**
 * 格式化数据
 * 把数据格式为由菜品Id为键值 对应为参与活动的数组
 * exap:
 * {
 *   '544124895': ['111','333']
 *   '459566664': ['222','333']
 * }
 */
export const initDishesRulesList = purchaseBenefitList => {
  const containDishObj = {};
  if (!purchaseBenefitList) {
    return [];
  }

  (purchaseBenefitList || []).forEach(purchaseBenefit => {
    const dishIds = purchaseBenefit.dishIds; // dishIds

    if (!dishIds) {
      // 关联Dish的Id
      return;
    }

    dishIds.forEach(dishId => {
      const { brandId, couponShared, description, id, name, planState, planType } = purchaseBenefit;
      const { ruleSubjectId, policySubjectId } = purchaseBenefit.marketRuleDtos[0];
      const otherBenefit = {
        brandId,
        couponShared,
        description,
        id,
        name,
        planState,
        planType,
        purchaseBenefitMethod: getPurchaseMethod(ruleSubjectId, policySubjectId),
      };

      if (!containDishObj[dishId]) {
        containDishObj[dishId] = [
          {
            benefit: otherBenefit,
            checked: true,
          },
        ];
      } else {
        containDishObj[dishId].push({
          benefit: otherBenefit,
          checked: false,
        });
      }
    });
  });

  return containDishObj;
};

/**
 * 格式化数据
 * 把数据格式为由活动Id为键值 对应为菜品数组
 * exap:
 * {
 *   '544124895': ['111','333']
 *   '459566664': ['222','333']
 * }
 */
export const initBenefitRulesList = (purchaseBenefitList, orderedDishesProps) => {
  const containBenefitObj = {};
  const containBenefitList = [];

  if (!purchaseBenefitList) {
    return [];
  }

  purchaseBenefitList.forEach(purchaseBenefit => {
    const purchaseBenefitId = purchaseBenefit.id;

    if (!containBenefitObj[purchaseBenefitId]) {
      containBenefitObj[purchaseBenefitId] = true;
      const { ruleSubjectId, ruleLogic, logicValue, policyDetailId, policyValue1, policyValue2, policySubjectId } = purchaseBenefit.marketRuleDtos[0];
      const purchaseBenefitMethod = getPurchaseMethod(ruleSubjectId, policySubjectId);

      containBenefitList.push({
        purchaseBenefitId,
        name: purchaseBenefit.name,
        dishIds: purchaseBenefit.dishIds,
        calculatRules: {
          ruleLogic,
          logicValue,
          policyDetailId,
          policyValue1,
          policyValue2,
          ruleSubjectId,
          policySubjectId,
          purchaseBenefitMethod,
        },
        couponShared: purchaseBenefit.couponShared,
      });
    }
  });

  purchaseCalculatorValidate(containBenefitList, orderedDishesProps);
  return containBenefitList;
};

/**
 * 根据菜品brandDishId获取选中的加价购或者满减的方案
 */
export const getPurchaseBenefitInfo = (dishesRules = {}, brandDishId) => {
  if (!brandDishId) {
    return;
  }

  const purchaseBenefitList = dishesRules[brandDishId] || [];
  const hasPurchaseBenefitList = !!purchaseBenefitList.length;
  const chosenPurchaseBenefit = purchaseBenefitList && purchaseBenefitList.find(dishRule => dishRule.checked);
  const hasChosenPurchaseBenefit = !!chosenPurchaseBenefit;

  return {
    hasPurchaseBenefitList,
    purchaseBenefitList,
    chosenPurchaseBenefit,
    hasChosenPurchaseBenefit,
  };
};

/**
 * 根据 加价购方案ID purchaseBenefitId 判断 activityDishes 是否存在被选中的 order/count 值
 */
export const getChosenActivityDish = (activityDishes, purchaseBenefitId, mode) => {
  return !!activityDishes[purchaseBenefitId].find(activityDish => activityDish[mode]);
};

/**
 * 查询所有菜品中是否存在加价购的分组(动态)
 */
export const getHasPurchaseBenefitGroup = dishes => {
  return !!dishes.find(dish => dish.purchaseBenefit);
};

/**
 * 格式化换购菜品的数据格式
 * @param {接口方案数据} purchaseBenefitList
 */
export const initActivityDishesList = purchaseBenefitList => {
  const activityDishList = {};

  if (!purchaseBenefitList) {
    return [];
  }

  purchaseBenefitList.forEach((item, index) => {
    const { policyDishes } = item;
    activityDishList[item.id] = [];
    const param = {
      count: 0,
      order: 0,
      activityBenefit: 0,
      activityBenefitNum: 0,
      marketPriceTotal: 0,
      realPriceTotal: 0,
    };

    policyDishes.forEach(temp => {
      const { dishType, midDishes } = temp;

      // 非中类
      if (dishType === MarkUpActivityDishType.SINGLEDISH) {
        activityDishList[item.id].push({ ...temp, ...param, marketPrice: temp.price, planId: item.id });

        return;
      }

      // 中类下的菜品, 要考虑到展示中类名称
      const midDishList = midDishes.map(itemx => ({ ...itemx, ...param, marketPrice: itemx.price, planId: item.id }));
      activityDishList[item.id].push(...midDishList);
    });
  });

  return activityDishList;
};

/**
 * 本阶段将加价购与其他优惠都互斥,还是启用之前单品优惠的 isMutex 字段
 */
export const removePurchaseAndDishPriviege = (serviceProps, dishes) => {
  const { isMutex } = serviceProps;
  const { activityDishes, dishesRules } = serviceProps.markUpBenefitProps;
  if (!activityDishes) {
    return isMutex;
  }

  let purchaseStatus = isMutex;
  const keys = Object.keys(activityDishes);

  // 如果所有菜品选了加价购但是没有加菜，此时应该将isMutex设置为true
  if (!isMutex) {
    const ruleKeys = Object.keys(dishesRules);
    const changes = [];
    ruleKeys.forEach(key => {
      const checked = dishesRules[key].filter(item => item.checked) || [];
      dishes.forEach(dish => {
        const { brandDishId } = dish;

        let benefit = [];
        if (typeof dish.order === 'number') {
          benefit = dish.benefitOptions;
        } else {
          benefit = dish.order[dish.order.length - 1].benefitOptions;
        }

        let checkedBenefit = [];

        if (benefit && benefit.length) {
          checkedBenefit = benefit.filter(ben => ben.isChecked);
        }

        if (checked.length && brandDishId === key) {
          changes.push(brandDishId);
        }
      });
    });

    if (changes.length === dishes.length) {
      purchaseStatus = true;
    }
  }

  // 在确认换购菜品加了菜的时候，设置的
  keys.forEach(key => {
    const checkedBenefit = activityDishes[key].find(activityDish => activityDish.order);

    if (checkedBenefit) {
      purchaseStatus = false;
    }
  });

  return purchaseStatus;
};

/**
 * setNoBenefitWithPurchasePrivilege 碰到与优惠券互斥的时候，设置选项为不选优惠
 * @param {obj} dish
 * @param {obj} serviceProps
 */
export const setNoBenefitWithPurchasePrivilege = (dish, serviceProps, callBack) => {
  const { dishesRules } = serviceProps.markUpBenefitProps;

  if (!dishesRules || !dish) {
    return;
  }

  let purchaseStatus = true;

  if (!dishesRules[dish.brandDishId]) {
    return true;
  }

  const purchaseBenefit = dishesRules[dish.brandDishId].filter(item => item.checked)[0];

  if (purchaseBenefit && purchaseBenefit.checked) {
    purchaseStatus = false;
  }

  return purchaseStatus;
};

/**
 * decentralizationCalculation 分权计算
 * @param {[]} dishes
 * @param {obj} calculateRules
 */
const decentralizationCalculation = (dishes, calculatRules) => {
  if (!calculatRules) {
    return dishes;
  }

  const { policyValue1, policyDetailId } = calculatRules;
  const { ChangePurchase, SendPurchase } = MarkUpPolicyDetail;
  let newDishes = [];

  // 优惠计算，只管换购还是赠送，不用管是满额还是满量 赠送商品
  if (policyDetailId === SendPurchase) {
    newDishes = dishes.map(dish => {
      if (!dish.order) {
        return {
          ...dish,
          activityBenefitNum: 0,
          activityBenefit: 0,
          marketPriceTotal: 0,
          realPriceTotal: 0,
        };
      }

      return {
        ...dish,
        activityBenefitNum: dish.order,
        activityBenefit: dish.order * dish.marketPrice,
        marketPriceTotal: dish.order * dish.marketPrice,
        realPriceTotal: 0,
      };
    });
  }

  // 换购商品
  if (policyDetailId === ChangePurchase) {
    let benefitAmount = 0;

    dishes.forEach(dish => {
      benefitAmount += dish.order * dish.marketPrice;
    });

    newDishes = dishes.map((dish, index) => {
      if (!dish.order) {
        return {
          ...dish,
          activityBenefitNum: 0,
          activityBenefit: 0,
          marketPriceTotal: 0,
          realPriceTotal: 0,
        };
      }

      const marketPriceWidthOrder = dish.order * dish.marketPrice; // 应付价
      let realPrice = (marketPriceWidthOrder / benefitAmount) * policyValue1; // 实际价

      // 换购商品的marketPrice 为 0
      if (benefitAmount === 0) {
        realPrice = !marketPriceWidthOrder && !index ? policyValue1 : 0;
      }

      const comparePrice = marketPriceWidthOrder - realPrice;

      return {
        ...dish,
        activityBenefitNum: dish.order,
        marketPriceTotal: marketPriceWidthOrder,
        realPriceTotal: Number(realPrice),
        activityBenefit: Math.max(comparePrice, 0),
      };
    });
  }

  return newDishes;
};

export const getActivityBenefitWithDish = serviceProps => {
  const { dishesRules, benefitRules, activityDishes } = serviceProps.markUpBenefitProps;

  if (!dishesRules || !(benefitRules || []).length || !activityDishes) {
    return {};
  }

  // 重新整理 activityDishes ,需要赋值 activityBenefitNum，activityBenefit 方便后续计算
  const newActivityDishesState = {};

  benefitRules.forEach(benefit => {
    const { calculatRules } = benefit;

    if (activityDishes[benefit.purchaseBenefitId]) {
      const dishes = activityDishes[benefit.purchaseBenefitId];

      // dishes 参与分权计算
      if (dishes.length) {
        const currentPrivDish = decentralizationCalculation(dishes, calculatRules);
        newActivityDishesState[benefit.purchaseBenefitId] = currentPrivDish;
      }
    }
  });

  return newActivityDishesState;
};

/**
 * 根据 purchaseBenefitMethod 定制展示信息以及商品分数的限制
 */
export const getPurchaseBenefitMethodBasicInfo = calculatRules => {
  const { logicValue, policyValue1, policyValue2, purchaseBenefitMethod } = calculatRules;
  let chosenpurchaseWord = '';
  let badgeName = '';
  let purchaseLimit = 0;

  switch (purchaseBenefitMethod.code) {
    case PurchaseMethod.FullSalesSend: {
      // 满额赠送
      badgeName = '满赠';
      chosenpurchaseWord = `满${logicValue}元可换购`;
      purchaseLimit = policyValue1;
      break;
    }

    case PurchaseMethod.FullSalesAdd: {
      // 满额加价
      badgeName = '加价购';
      chosenpurchaseWord = `满${logicValue}元再加${policyValue1}元可换购`;
      purchaseLimit = policyValue2;
      break;
    }

    case PurchaseMethod.FullVolumeSend: {
      // 满量赠送
      badgeName = '满赠';
      chosenpurchaseWord = `满${logicValue}份可换购`;
      purchaseLimit = policyValue1;
      break;
    }

    case PurchaseMethod.FullVolumeAdd: {
      // 满量加价
      badgeName = '加价购';
      chosenpurchaseWord = `满${logicValue}份再加${policyValue1}元可换购`;
      purchaseLimit = policyValue2;
      break;
    }

    default:
      break;
  }

  return { chosenpurchaseWord, purchaseLimit, badgeName };
};

export const getFinalDishesWidthPurchase = markUpBenefitProps => {
  const { activityDishes } = markUpBenefitProps;

  if (!activityDishes) {
    return { finalDishes: [], purchaseRealTotal: 0, purchaseMarketTotal: 0 };
  }

  let compareBenefit = 0;
  const finalDishes = [];
  for (let key in activityDishes) {
    let marketTotal = 0;
    let realTotal = 0;
    activityDishes[key].forEach(dish => {
      if (dish.order > 0) {
        marketTotal += dish.marketPriceTotal;
        realTotal += dish.realPriceTotal;
        finalDishes.push(dish);
      }
    });

    if (realTotal < marketTotal) {
      compareBenefit = marketTotal - realTotal;
    }
  }

  let purchaseRealTotal = 0;
  let purchaseMarketTotal = 0;

  finalDishes.forEach(dish => {
    purchaseRealTotal += dish.realPriceTotal;
    purchaseMarketTotal += dish.marketPrice * dish.order;
  });

  return { finalDishes, purchaseRealTotal, purchaseMarketTotal, compareBenefit };
};

/**
 * resetPurChaseBenefitReducerMethod
 */
export const resetPurChaseBenefitReducerMethod = (nextState, benefitRules, noShowPurchaseBtn) => {
  let newNextState = nextState;
  benefitRules.forEach(item => {
    if (noShowPurchaseBtn && item.showPurchaseBtn) {
      // 是否需要排除item.showPurchaseBtn为true的情况
      return;
    }

    newNextState = newNextState.updateIn(['serviceProps', 'markUpBenefitProps', 'activityDishes', item.purchaseBenefitId], dishes =>
      dishes.flatMap(dish =>
        dish
          .set('count', 0)
          .set('order', 0)
          .set('activityBenefitNum', 0)
          .set('activityBenefit', 0)
          .set('marketPriceTotal', 0)
          .set('realPriceTotal', 0)
      )
    );
  });

  return newNextState;
};

/**
 * setIsMutexReducerMethod
 */
export const setIsMutexReducerMethod = (nextState, coverParams) => {
  const newNextState = nextState.setIn(
    ['serviceProps', 'isMutex'],
    getPurchaseAndBenefitShared({
      benefitProps: nextState.serviceProps.activityBenefitProps,
      orderedDishes: nextState.orderedDishesProps.dishes,
      needAddBenefit: false,
      serviceProps: nextState.serviceProps,
      ...coverParams,
    })
  );

  return newNextState;
};



// WEBPACK FOOTER //
// ./src/helper/privilege/change-purchase-calculator.js